import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useSidebarOpenState } from '@backstage/core-components';
import { manewcoTokens } from '@platform/plugin-platform-react';

const useStyles = makeStyles(theme => {
  const MANEWCO = manewcoTokens(theme);
  return {
    toggle: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      width: '100%',
      minHeight: 48,
      padding: '0 8px 0 24px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      textAlign: 'left',
      color: MANEWCO.navText,
      fontFamily: theme.typography.fontFamily,
      // Un cran sous les entrées : « Accès & administration » et son chevron tiennent dans 224 px.
      fontSize: '0.79rem',
      fontWeight: 500,
      letterSpacing: -0.1,
      '&:hover': { backgroundColor: MANEWCO.hover },
    },
    label: { flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
    chevron: { flex: 'none', fontSize: '1.1rem', transition: 'transform 120ms' },
    children: { borderLeft: `1px solid ${MANEWCO.line}`, marginLeft: 20 },
  };
});

interface Props {
  label: string;
  icon: ComponentType<any>;
  /** Chemins des entrées du groupe : le groupe s'ouvre seul quand la page courante en fait partie. */
  paths: { path: string; exact?: boolean }[];
  children: ReactNode;
}

const normalize = (p: string) => `/${p.replace(/^\/+/, '')}`;

/** Groupe repliable du menu (maquette : « Accès & administration », « Catalogue & outils »). */
export const SidebarExpandableGroup = ({ label, icon: Icon, paths, children }: Props) => {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const { pathname } = useLocation();
  const current = paths.some(
    ({ path, exact }) => pathname === normalize(path) || (!exact && pathname.startsWith(`${normalize(path)}/`)),
  );
  const [open, setOpen] = useState(current);
  // Arrivée sur une page du groupe sans passer par le menu (lien, recherche, retour) : on l'ouvre,
  // sans jamais le refermer de force.
  useEffect(() => {
    if (current) setOpen(true);
  }, [current]);

  // Menu replié en rail : pas d'en-tête de groupe, les entrées restent atteignables.
  if (!isOpen) return <>{children}</>;

  return (
    <>
      <button type="button" className={classes.toggle} aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <Icon fontSize="small" />
        <span className={classes.label}>{label}</span>
        <span aria-hidden className={classes.chevron} style={{ transform: open ? 'rotate(90deg)' : 'none' }}>
          ›
        </span>
      </button>
      {open && <div className={classes.children}>{children}</div>}
    </>
  );
};
